import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, PillTabs } from '../../../school_panel/components/ui';
import { fieldClass } from '../../../school_panel/lib/fieldClass';
import { apiErrorText } from '../../../school_panel/lib/apiError';
import { SchoolApi } from '../../../school_panel/api/client';
import { useApproveJoinRequest } from '../../../school_panel/api/hooks';
import { ROLL_MAX } from '../../../school_panel/lib/roll';

export type JoinRequestRow = {
  id: number;
  full_name?: string;
  grade_name?: string | null;
  grade_id?: number | null;
  section_id?: number | null;
  roll_number?: number | null;
  suggested_roll?: number | null;
  created?: string;
};

// Design `.fl`: 12px / 600 muted field label.
const PLACE_LABEL = 'text-xs font-semibold text-muted';

type Props = {
  request: JoinRequestRow | null;
  onClose: () => void;
  /** Called with a one-line summary after the request is approved and placed. */
  onApproved?: (summary: string) => void;
};

/**
 * School mode 1b-i: approve a join request and set grade, section and roll. Design `.place` panel: it
 * renders inline, inside the request's row, instead of a modal.
 */
export default function ApproveRequestDialog({ request, onClose, onApproved }: Props) {
  const gradesQ = useQuery({
    queryKey: ['analytics.grades'],
    queryFn: () => SchoolApi.analyticsGrades(),
    enabled: !!request,
  });
  const approve = useApproveJoinRequest();
  const grades = useMemo(() => gradesQ.data?.grades ?? [], [gradesQ.data]);

  const panelRef = useRef<HTMLFormElement>(null);
  const [gradeId, setGradeId] = useState<number | null>(null);
  const [sectionId, setSectionId] = useState<number | null>(null);
  const [roll, setRoll] = useState('');
  const [taken, setTaken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The panel opens inline in the row, so move keyboard focus into it.
  useEffect(() => { if (request) panelRef.current?.focus(); }, [request?.id]);

  useEffect(() => {
    if (!request) return;
    setSectionId(request.section_id ?? null);
    const initial = request.roll_number ?? request.suggested_roll;
    setRoll(initial != null ? String(initial) : '');
    setTaken(null);
    setError(null);
  }, [request]);

  useEffect(() => {
    if (!request) return;
    const known = grades.some((g) => g.id === request.grade_id);
    setGradeId(known ? (request.grade_id as number) : null);
  }, [request, grades]);

  const sections = useMemo(
    () => grades.find((g) => g.id === gradeId)?.sections ?? [],
    [grades, gradeId],
  );
  const rollNum = roll.trim() === '' ? null : Number(roll);
  const rollInvalid = rollNum !== null && (!Number.isInteger(rollNum) || rollNum < 1 || rollNum > ROLL_MAX);
  const busy = approve.isPending;
  // Swapping only makes sense when the student already holds a roll in the
  // chosen grade; otherwise the holder would be left without a number.
  const canSwap = request?.roll_number != null && gradeId === request.grade_id;

  const submit = async (swap = false) => {
    if (!request || rollInvalid || busy) return;
    setError(null);
    try {
      await approve.mutateAsync({
        id: request.id,
        body: {
          ...(gradeId != null ? { grade_id: gradeId } : {}),
          section_id: sectionId,
          roll_number: rollNum,
          ...(swap ? { swap: true } : {}),
        },
      });
      const grade = grades.find((g) => g.id === gradeId);
      const sec = sections.find((x) => x.id === sectionId);
      onApproved?.(
        `Approved ${request.full_name || 'student'}${grade ? ` · ${grade.name}` : ''}${sec ? ` · ${sec.display || sec.name}` : ''}${rollNum != null ? ` · roll ${rollNum}` : ''}.`,
      );
      onClose();
    } catch (e: any) {
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.code === 'roll_taken') {
        setTaken(String(data.detail));
      } else {
        setError(apiErrorText(e, 'Could not approve this request. Try again.'));
      }
    }
  };

  const requestedGrade = request?.grade_name || grades.find((g) => g.id === request?.grade_id)?.name || 'no grade set';
  const requestedSection = request?.section_id != null
    ? grades.flatMap((g) => g.sections ?? []).find((x) => x.id === request.section_id)
    : undefined;
  const requestedLabel = `${requestedGrade}${requestedSection ? ` · ${requestedSection.display || requestedSection.name}` : ' · no section chosen'}`;
  const firstName = (request?.full_name || 'student').trim().split(/\s+/)[0];

  const close = () => { if (!busy) onClose(); };
  if (!request) return null;

  return (
    <form
      ref={panelRef}
      tabIndex={-1}
      role="group"
      aria-label={`Approve and place ${request.full_name || 'student'}`}
      className="box-border flex basis-full flex-wrap items-end gap-3 rounded-[16px] border border-line bg-note p-4 outline-none"
      onSubmit={(e) => { e.preventDefault(); submit(taken !== null && canSwap); }}>
      <h3 className="basis-full font-display text-[15px] font-bold text-ink">Place {firstName} in a class</h3>
      <p className="-mt-1.5 basis-full text-[13px] text-muted">
        Requested {requestedLabel}. Suggested roll is the next free number in that section.
      </p>
      <div className="min-w-0 basis-full">
        <span className={`${PLACE_LABEL} mb-1.5 block`}>Grade</span>
        <PillTabs scroll dense label="Grade" value={gradeId == null ? '' : String(gradeId)}
          onChange={(k) => {
            setGradeId(Number(k));
            setSectionId(null);
            setRoll('');
            setTaken(null);
          }}
          options={grades.map((g) => ({ key: String(g.id), label: g.name }))} />
      </div>
      {sections.length > 0 && (
        <div className="min-w-0 basis-full">
          <span className={`${PLACE_LABEL} mb-1.5 block`}>Section</span>
          <PillTabs scroll dense label="Section" value={sectionId == null ? 'none' : String(sectionId)}
            onChange={(k) => setSectionId(k === 'none' ? null : Number(k))}
            options={[{ key: 'none', label: 'No section yet' }, ...sections.map((x) => ({ key: String(x.id), label: x.display || x.name }))]} />
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="approve-roll" className={PLACE_LABEL}>Roll number</label>
        <input
          id="approve-roll"
          type="number"
          min={1}
          max={ROLL_MAX}
          aria-invalid={rollInvalid || undefined}
          className={`${fieldClass} min-h-[40px] w-[90px]`}
          value={roll}
          onChange={(e) => { setRoll(e.target.value); setTaken(null); }}
        />
        {rollInvalid && <span className="text-xs text-danger-ink">Must be 1–9999.</span>}
      </div>
      <span className="flex-1" />
      <Button variant="ghost" size="sm" type="button" onClick={close} disabled={busy}>Cancel</Button>
      <Button
        variant="primary"
        size="sm"
        type="submit"
        loading={busy}
        disabled={busy || rollInvalid}>
        {taken && canSwap ? 'Swap numbers' : 'Approve and place'}
      </Button>
      {taken && <p role="alert" className="basis-full text-sm text-warning-ink">{taken}</p>}
      {error && <p role="alert" className="basis-full text-sm text-danger-ink">{error}</p>}
    </form>
  );
}
